<template>
  <div
    id="appvue"
    :class="{
      'popup-info': isPopup,
    }"
    v-if="isLoad"
  >
    <Header v-if="isBlank" />
    <main 
      :class="{
        'main-content': isBlank,
        main: isMain,
        'expand-report': isExpandReport,
        'drug-info': isDrugInfo,
        'popup-info': isPopup,
      }"
    >
      <router-view></router-view>
    </main>
    <Footer v-if="isBlank" />
    <Aside v-if="isBlank" />
  </div>
  <AlertDialog />
  <div v-if="loading" class="loading-overlay">
    <img src="@/assets/img/loadingbar.png" alt="loading" />
  </div>
</template>

<script>
import { defineComponent, computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useCommonStore } from "@/store/common";
import { useContentsStore } from "@/store/contents";
import Header from "./components/Header.vue";
import Footer from "./components/Footer.vue";
import Aside from "./components/Aside.vue";
import AlertDialog from "@/components/AlertDialog2.vue";
import { getUser } from "@/lib/loginService";
import ubistApi from "@/lib/ubistApi";

export default defineComponent({
  name: "App",
  components: {
    Header,
    Footer,
    Aside,
    AlertDialog,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { locale, t } = useI18n();

    const commonStore = useCommonStore();
    const contentsStore = useContentsStore();
    const { lang, loading, user, userServiceList, productType, customer } = storeToRefs(commonStore);
    const { changeLang } = storeToRefs(contentsStore);

    const isLoad = ref(false);

    const isMain = computed(() => route.name === "main");
    const isBlank = computed(
      () => !["ExpandReport", "DrugInfo", "ImagePopup", "Auth", "Refresh", "Callback", "AuthP", "AuthC", "DrugInfomation"].includes(route.name)
    );
    const isExpandReport = computed(() => route.name === "ExpandReport");
    const isDrugInfo = computed(() => route.name === "DrugInfo");
    const isPopup = computed(() => route.name === "ImagePopup");
    const result = ref([]);

    // 공통 함수: 세션 확인 및 재로그인 시도
    const checkSessionWithRetry = async (productType) => {
      try {
        let sessionResult = await ubistApi.getSession(productType);
        // console.log('Initial session check:', sessionResult);
        
        if (sessionResult.errorCode !== 0) {
          // console.log('Session failed, attempting re-login...');
          // 재로그인 시도
          const userResult = await getUser();
          if (userResult) {
            user.value = userResult;
            sessionResult = await ubistApi.getSession(productType);
            // console.log('Re-login session check:', sessionResult);
          }
          
          if (sessionResult.errorCode !== 0) {
            // console.log('Re-login failed, redirecting to login');
            window.location.href = process.env.VUE_APP_TEAM_UBIST;
            return false;
          }
        }
        return true;
      } catch (error) {
        // console.error('Error in session check:', error);
        window.location.href = process.env.VUE_APP_TEAM_UBIST;
        return false;
      }
    };

    // 공통 함수: 사용자 데이터 초기화
    const initializeUserData = async (targetProductType) => {
      try {
        // ProductType 에 따른 메뉴 목록 가져오기
        await commonStore.getMenuList(targetProductType);

        // 고객 구독 서비스 목록 가져오기
        userServiceList.value = await ubistApi.getServices(targetProductType);

        // productType 스토어 값 업데이트
        productType.value = targetProductType;

        // 고객정보 가져오기
        const customerRes = await ubistApi.getCustomer(targetProductType);
        if (customerRes) {
          customer.value = customerRes.data;
          // console.log('Customer data updated:', customerRes);
        }

        return true;
      } catch (error) {
        // console.error('Error initializing user data:', error);
        return false;
      }
    };

    // 공통 함수: 전체 사용자 로그인 처리
    const handleUserLogin = async (targetProductType) => {
      if (process.env.NODE_ENV === "local") return true;

      if (!targetProductType) {
        window.location.href = process.env.VUE_APP_TEAM_UBIST;
        return false;
      }

      // 사용자 정보 확인
      if (!user.value) {
        const userResult = await getUser();
        if (!userResult) {
          window.location.href = process.env.VUE_APP_TEAM_UBIST;
          return false;
        }
        user.value = userResult;
      }

      // 세션 확인 및 재로그인 시도
      const sessionValid = await checkSessionWithRetry(targetProductType);
      if (!sessionValid) return false;

      // 사용자 데이터 초기화
      const dataInitialized = await initializeUserData(targetProductType);
      if (!dataInitialized) return false;

      return true;
    };

    watch([route], async () => {
      // console.log('route', route.name);

      if (!["Auth", "Refresh", "Callback", "DrugInfomation"].includes(route.name)) {
        const lProductType = localStorage.getItem('productType');
        const loginSuccess = await handleUserLogin(lProductType);
        
        if (loginSuccess) {
          // productType 불일치 체크 (기존 로직 유지)
          if (productType.value != null && productType.value != lProductType) {
            window.location.href = process.env.VUE_APP_TEAM_UBIST;
          } else if (productType.value == null && lProductType) {
            productType.value = lProductType;
          }
        }
      }
      
      isLoad.value = true;
    });

    // Login route
    const logout = () => {
      commonStore.logout();
    };

    onMounted(async () => {
      // console.log('App onMounted');
      lang.value = localStorage.getItem("lang");

      // productType 변경 처리 함수 (간소화됨)
      const handleProductTypeChange = async (newProductType) => {
        if (newProductType) {
          const loginSuccess = await handleUserLogin(newProductType);
          
          if (loginSuccess) {
            // 메인 페이지로 이동
            if (route.name !== 'main') {
              router.push('/');
            }
            console.log('productType storage event - changed to:', newProductType);
          }
        }
      };

      window.addEventListener('storage', (event) => {
        if (event.key === 'lang') {
          const newVal = event.newValue;
          lang.value = newVal;
          locale.value = newVal;
          changeLang.value = true;
        } else if (event.key === 'productType') {
          const newProductType = event.newValue;
          handleProductTypeChange(newProductType);
        }
      });

      if (process.env.NODE_ENV === "local") {
        localStorage.setItem('productType', 1);
      }
    });

    return {
      isLoad,
      isMain,
      isBlank,
      isExpandReport,
      isDrugInfo,
      isPopup,
      loading,
    };
  },
  methods: {},
  created() {
    let locale = localStorage.getItem("lang");
    if (!locale) locale = (navigator.language || navigator.userLanguage).substr(0, 2);
    locale = locale !== "ko" ? "en" : locale;
    this.$i18n.locale = locale;
    localStorage.setItem("lang", this.$i18n.locale);
    // 환경 변수 출력
    // console.log("process.env : ", process.env);
  },
});
</script>

<style scoped>
.expand-report {
  margin: 0;
  padding: 0;
  padding-bottom: 10px;
  height: 100vh;
  width: 100vw;
}
.drug-info {
  padding: 25px;
  height: 100vh;
  width: 100vw;
}

.popup-info {
  overflow: hidden;
  height: 100vh;
  min-width: 0px !important;
}


/* 로딩바 */
.loading-overlay {
  z-index: 99999;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5); /* 로딩바 배경색 */
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-overlay img {
  width: 70px; /* 로딩바 이미지 크기 */
  height: 70px;
  animation: spin 2s infinite linear; /* 로딩바 애니메이션 설정 */
}

@keyframes spin {
  from {
    transform: rotate(0deg); /* 로딩바 초기 각도 */
  }
  to {
    transform: rotate(360deg); /* 로딩바 회전 각도 */
  }
}
</style>
